import React from 'react'
import {Avatar, Box, Button, Divider, Link, ListItemIcon, ListItemText, MenuItem, MenuList, Popover, Stack, Typography} from "@mui/material"
import BrandMark from "./BrandMark"
import {Link as RouterLink} from "react-router-dom"
import {RoleCheck} from "../../../library/RoleCheck"
import {useAuth0} from "@auth0/auth0-react"
import {useApi} from "../../../hooks/useApi"
import CheckoutButton from "../../billing/CheckoutButton"
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined'
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined'
import SwapHorizOutlinedIcon from '@mui/icons-material/SwapHorizOutlined'
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined'
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined'
import Logo from "../../accounts/Logo"
import {resolveNameForRole} from "../../../api/enum/MembershipRole"
import NavBreadcrumbs from "./NavBreadcrumbs"
import {TOP_NAV_HEIGHT} from "../layoutConstants"

function getInitials(fullName?: string): string {
    if (!fullName) return "?"
    const parts = fullName.trim().split(/\s+/)
    if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? "?"
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function NavBar() {
    const [popoverAnchor, setPopoverAnchor] = React.useState<HTMLElement>()

    const {currentUser, currentOrganization} = useApi()

    const { logout } = useAuth0()
    const { clearAll } = useApi()

    const handleLogout = () => {
        clearAll()
        logout()
    }

    return (
        <Stack
            component="nav"
            direction="row"
            justifyContent="space-between"
            sx={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                zIndex: 1200,
                height: `${TOP_NAV_HEIGHT}px`,
                px: 3,
                bgcolor: '#fff',
                boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
                alignItems: 'center',
            }}
        >
            <Stack direction="row" alignItems="center" spacing={3}>
                <Link
                    component={RouterLink}
                    to="/"
                    sx={{height: 32, width: 32, display: 'flex', alignItems: 'center'}}
                >
                    <BrandMark/>
                </Link>
                <NavBreadcrumbs/>
            </Stack>

            <Stack direction="row" spacing={2} alignItems="center">
                <Button
                    startIcon={<OpenInNewOutlinedIcon/>}
                    href="https://docs.entrypointai.com/"
                    target="_blank"
                    rel="noopener"
                    sx={{display: {lg: "flex", xs: "none"}}}
                >
                    Docs
                </Button>

                {RoleCheck.isAdmin() &&
                    <Button
                        variant="outlined"
                        startIcon={<PersonAddOutlinedIcon/>}
                        component={RouterLink}
                        to="/organization/members"
                    >
                        Invite Your Team
                    </Button>
                }

                {!currentOrganization?.billingDetails?.activePlan &&
                    <CheckoutButton>Upgrade</CheckoutButton>
                }

                <Avatar
                    onClick={(event) => setPopoverAnchor(event.currentTarget)}
                    sx={{
                        width: 32,
                        height: 32,
                        bgcolor: '#4c49c1',
                        fontSize: '0.85rem',
                        cursor: 'pointer',
                    }}
                >
                    {getInitials(currentUser?.fullName)}
                </Avatar>
            </Stack>

            <Popover
                open={Boolean(popoverAnchor)}
                anchorEl={popoverAnchor}
                onClose={() => setPopoverAnchor(undefined)}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Box sx={{width: 280}}>
                    {/* User identity */}
                    <Stack direction="row" spacing={2} alignItems="center" sx={{px: 2.5, pt: 2.5, pb: 1}}>
                        <Avatar
                            src={currentUser?.avatar?.sizes?.thumbnail}
                            sx={{width: 40, height: 40, bgcolor: '#4c49c1', fontSize: '0.95rem'}}
                        >
                            {getInitials(currentUser?.fullName)}
                        </Avatar>
                        <Stack sx={{minWidth: 0}}>
                            <Typography sx={{fontWeight: 600, fontSize: '0.9rem', lineHeight: 1.3}}>
                                {currentUser?.fullName}
                            </Typography>
                            <Typography sx={{fontSize: '0.8rem', color: '#888', lineHeight: 1.3}} noWrap>
                                {currentUser?.email}
                            </Typography>
                        </Stack>
                    </Stack>

                    <MenuList sx={{py: 0.5}}>
                        <MenuItem
                            component={RouterLink}
                            to="/user"
                            onClick={() => setPopoverAnchor(undefined)}
                            sx={{py: 1, px: 2.5}}
                        >
                            <ListItemIcon><PersonOutlinedIcon fontSize="small"/></ListItemIcon>
                            <ListItemText primaryTypographyProps={{fontSize: '0.875rem'}}>
                                My Account
                            </ListItemText>
                        </MenuItem>
                    </MenuList>

                    <Divider/>

                    {/* Organization context + actions */}
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{px: 2.5, pt: 2, pb: 1, minWidth: 0}}>
                        <Logo image={currentOrganization?.logo}/>
                        <Stack sx={{minWidth: 0, flex: 1}}>
                            <Typography sx={{fontWeight: 500, fontSize: '0.85rem', lineHeight: 1.3}} noWrap>
                                {currentOrganization?.name}
                            </Typography>
                            {currentOrganization?.role &&
                                <Typography sx={{fontSize: '0.75rem', color: '#999', lineHeight: 1.3}}>
                                    {resolveNameForRole(currentOrganization.role)}
                                </Typography>
                            }
                        </Stack>
                    </Stack>

                    <MenuList sx={{py: 0.5}}>
                        {RoleCheck.isAdmin() &&
                            <MenuItem
                                component={RouterLink}
                                to="/organization"
                                onClick={() => setPopoverAnchor(undefined)}
                                sx={{py: 1, px: 2.5}}
                            >
                                <ListItemIcon><SettingsOutlinedIcon fontSize="small"/></ListItemIcon>
                                <ListItemText primaryTypographyProps={{fontSize: '0.875rem'}}>
                                    Settings
                                </ListItemText>
                            </MenuItem>
                        }
                        {RoleCheck.isAdmin() &&
                            <MenuItem
                                component={RouterLink}
                                to="/organization/members"
                                onClick={() => setPopoverAnchor(undefined)}
                                sx={{py: 1, px: 2.5}}
                            >
                                <ListItemIcon><PeopleOutlinedIcon fontSize="small"/></ListItemIcon>
                                <ListItemText primaryTypographyProps={{fontSize: '0.875rem'}}>
                                    Members
                                </ListItemText>
                            </MenuItem>
                        }
                        {RoleCheck.isAdmin() &&
                            <MenuItem
                                component={RouterLink}
                                to="/organization?tab=billing"
                                onClick={() => setPopoverAnchor(undefined)}
                                sx={{py: 1, px: 2.5}}
                            >
                                <ListItemIcon><PaymentOutlinedIcon fontSize="small"/></ListItemIcon>
                                <ListItemText primaryTypographyProps={{fontSize: '0.875rem'}}>
                                    Billing
                                </ListItemText>
                            </MenuItem>
                        }
                        <MenuItem
                            component={RouterLink}
                            to="/organizations/select"
                            onClick={() => setPopoverAnchor(undefined)}
                            sx={{py: 1, px: 2.5}}
                        >
                            <ListItemIcon><SwapHorizOutlinedIcon fontSize="small"/></ListItemIcon>
                            <ListItemText primaryTypographyProps={{fontSize: '0.875rem'}}>
                                Switch Organization
                            </ListItemText>
                        </MenuItem>
                    </MenuList>

                    <Divider/>

                    {/* Logout */}
                    <MenuList sx={{py: 0.5}}>
                        <MenuItem
                            onClick={handleLogout}
                            sx={{py: 1.25, px: 2.5, color: '#888'}}
                        >
                            <ListItemIcon><LogoutOutlinedIcon fontSize="small" sx={{color: '#888'}}/></ListItemIcon>
                            <ListItemText primaryTypographyProps={{fontSize: '0.875rem'}}>
                                Log Out
                            </ListItemText>
                        </MenuItem>
                    </MenuList>
                </Box>
            </Popover>
        </Stack>
    )
}

export default NavBar
